<script lang="ts" setup>
import { computed, onMounted, ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  IconFileMd as DocumentIcon,
  IconImageXXL as ImageIcon,
  IconVideoXXL as VideoIcon,
  IconLinkMd as LinkIcon,
  IconPhoneMd as PhoneIcon,
} from "@profeat/ui-kit";

import { FileData, useBroadcast } from "~/stores/broadcast.store";
import { useContacts } from "~/stores/contacts.store";
import {
  TemplateComponentButtonType,
  TemplateComponentFormat,
  TemplateButtonProperty,
  WabaChannelTemplate,
  TemplateContentType,
} from "~/entities/template";
import { convertTo } from "~/utils/file";
import { createTemplateHtml } from "~/utils/helpers";
import { TemplateVariablesMap } from "./forms/fields/FieldTemplateVariables/types";

const headerFormat = {
  [TemplateContentType.IMAGE]: {
    icon: ImageIcon,
    className: TemplateContentType.IMAGE.toLowerCase(),
  },
  [TemplateContentType.DOCUMENT]: {
    icon: DocumentIcon,
    className: TemplateComponentFormat.DOCUMENT.toLowerCase(),
  },
  [TemplateContentType.VIDEO]: {
    icon: VideoIcon,
    className: TemplateContentType.VIDEO.toLowerCase(),
  },
};

const { t } = useI18n();
const broadcastStore = useBroadcast();

const props = defineProps<{
  template: WabaChannelTemplate;
  variables: TemplateVariablesMap;
  fieldMap: Record<string, string>;
  headerFile: FileData | null;
}>();

const headerText = ref<string>();
const body = ref<string>();
const footer = ref<string>();
const buttons = ref<TemplateButtonProperty[]>([]);

const headerType = computed<TemplateContentType | null>(() => {
  if (
    props.template &&
    Object.values(TemplateContentType).includes(props.template.type)
  ) {
    return props.template.type;
  }
  return null;
});
const documentSize = computed(() => {
  if (broadcastStore.getHeaderFileV2) {
    return `${convertTo(broadcastStore.headerFileV2!.fileBinary.size, "MB")}`;
  }
  return "";
});

const fileContent = computed(() => props.headerFile?.fileContent);
const fileMimeType = computed(() => props.headerFile?.fileMimetype);
const fileName = computed(() => props.headerFile?.fileName || null);
const fileExtension = computed(() => props.headerFile?.fileExtension || null);

watch(
  () => props.variables.bodyVars,
  (vars) => {
    const template = props.template;
    if (template && template.body) {
      body.value = getHtml(template.body, vars);
    }
  },
  {
    deep: true,
  }
);

watch(
  () => props.variables.headerVars,
  (vars) => {
    const template = props.template;
    if (
      template &&
      template.header &&
      unref(headerType) === TemplateContentType.TEXT
    ) {
      headerText.value = getHtml(template.header, vars);
    }
  },
  {
    deep: true,
  }
);

watch(
  () => props.variables.footerVars,
  (vars) => {
    const template = props.template;
    if (template && template.footer) {
      footer.value = getHtml(template.footer, vars);
    }
  },
  {
    deep: true,
  }
);

watch(
  () => props.variables.buttonVars,
  (vars) => {
    const template = props.template;
    if (template && template.buttons?.length) {
      buttons.value = unref(buttons)?.map((button) => {
        button.text = getHtml(button.text, vars);
        return button;
      });
    }
  },
  {
    deep: true,
  }
);

watch(
  () => props.fieldMap,
  () => {
    updateText();
  },
  {
    deep: true,
  }
);

onMounted(() => {
  updateText();
});

function updateText() {
  const template = props.template;
  if (!template) return;
  if (template.header && unref(headerType) === TemplateContentType.TEXT) {
    headerText.value = getHtml(template.header, props.variables.headerVars);
  }
  if (template.body) {
    body.value = getHtml(template.body, props.variables.bodyVars);
  }
  if (template.buttons) {
    buttons.value = template.buttons.map((button) => {
      button.text = getHtml(button.text, props.variables.buttonVars);
      return button;
    });
  }
}

function getHtml(text: string, variables: Record<string, string>): string {
  return createTemplateHtml(text, variables, props.fieldMap);
}
</script>

<template>
  <div class="message-preview">
    <p class="title">{{ $t("broadcast.previewForm.preview") }}</p>

    <div class="preview">
      <header v-if="headerType" class="header">
        <div v-if="headerType === TemplateContentType.TEXT" class="header-text">
          <p v-html="headerText" />
        </div>

        <div v-else :class="`header-${headerFormat[headerType].className}`">
          <div v-if="fileContent" class="file-preview">
            <img
              v-if="headerType === TemplateContentType.IMAGE"
              :src="fileContent"
              alt="Red dot"
            />

            <video
              v-else-if="
                headerType === TemplateContentType.VIDEO && fileContent
              "
              controls
            >
              <source :src="fileContent" :type="fileMimeType" />
            </video>

            <div
              v-else-if="headerType === TemplateContentType.DOCUMENT"
              class="document"
            >
              <component
                :is="headerFormat[headerType].icon"
                class="color-[#b6c7d6]"
              />
              <div class="document-description">
                <p class="title" v-show="fileName">
                  {{ fileName }}
                </p>
                <p class="size" v-show="fileExtension">
                  {{ documentSize }} MB &#183;
                  {{ fileExtension }}
                </p>
              </div>
            </div>
          </div>

          <div v-else class="file-empty">
            <component
              :is="headerFormat[headerType].icon"
              class="icon color-[#b6c7d6]"
            />
          </div>
        </div>
      </header>

      <div v-if="body" class="body">
        <p v-html="body" />
      </div>

      <footer v-if="footer" class="footer">
        <p v-html="footer" />
      </footer>
    </div>

    <div v-if="buttons?.length" class="buttons">
      <button v-for="(button, index) in buttons" :key="`button-${index}`">
        <LinkIcon v-if="button.type === TemplateComponentButtonType.URL" />
        <PhoneIcon
          v-if="button.type === TemplateComponentButtonType.PHONE_NUMBER"
        />

        {{ button.text }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.message-preview {
  .title {
    color: var(--common-black, #2b3238);
    font-size: 16px;
    font-weight: 600;
    line-height: 150%;
    margin-bottom: 16px;
  }

  .preview {
    padding: 16px;
    border-radius: 16px;
    background: var(--common-fog, #f1f5f9);
    display: flex;
    flex-direction: column;
    gap: 16px;

    .header {
      &-text {
        color: var(--common-black, #2b3238);
        font-size: 16px;
        font-weight: 600;
        line-height: 150%;
      }
      &-image {
        border-radius: 8px;
        border: 1px dashed var(--common-dull-sky, #b6c7d6);
        width: 100%;
        height: 184px;

        .file-preview {
          width: 100%;
          height: 100%;

          img {
            border-radius: 8px;
            padding: 2px;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }
      &-document {
        border-radius: 8px;
        border: 1px dashed var(--common-dull-sky, #b6c7d6);
        .file-empty {
          padding: 12.5px 0;
        }

        .document {
          padding: 8px 18px;
          display: flex;
          gap: 14px;
          align-items: center;

          &-description {
            width: 100%;

            .title {
              color: var(--common-black, #2b3238);
              font-size: 16px;
              line-height: 150%;
              margin: 0;
            }

            .size {
              color: var(--common-asphalt, #91a0ae);
              font-size: 12px;
              line-height: 133.3%;
            }
          }
        }
      }
      &-video {
        border-radius: 8px;
        border: 1px dashed var(--common-dull-sky, #b6c7d6);
        width: 100%;
        height: 184px;

        .file-preview {
          width: 100%;
          height: 100%;

          video {
            border-radius: 8px;
            padding: 2px;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
      }

      .file-empty {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .body {
      color: var(--common-black, #2b3238);
      font-size: 16px;
      line-height: 150%;
    }

    .footer {
      color: var(--common-black, #2b3238);
      font-size: 12px;
      line-height: 133.3%;
    }
  }

  .buttons {
    display: flex;
    margin-top: 4px;
    gap: 4px;
    flex-wrap: wrap;

    button {
      border-radius: 12px;
      background: var(--common-fog, #f1f5f9);
      padding: 8px;
      min-width: calc(50% - 4px);

      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      flex-shrink: 1;

      color: var(--additional-blue, #1c7ef1);
      text-align: center;
      font-size: 16px;
      line-height: 150%;
    }
  }
}

:global(.variable-slot) {
  border-radius: 8px;
  border: 1px solid var(--common-rain);
  background: var(--common-white);
  padding: 0 8px;
  color: var(--common-dull-sky);
  font-size: 16px;
  line-height: 150%;
}

:global(.field-variable) {
  border-radius: 8px;
  color: var(--additional-blue);
}
</style>
